import { useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { createRouter, RouterProvider } from '@tanstack/react-router';

import { exampleCustomer, keboolaPortal } from '@keboola/brand-registry';
import { type ColorScheme, ThemeProvider } from '@keboola/design';

import { persistScheme, readPersistedScheme } from './colorScheme';
// Generated by @tanstack/router-plugin on `pnpm dev`/`pnpm build`; committed so type-check passes.
import { routeTree } from './routeTree.gen';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      retry: 1,
    },
  },
});

const router = createRouter({ routeTree });

// Module augmentation requires `interface`.
declare module '@tanstack/react-router' {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Register {
    router: typeof router;
  }
}

/**
 * `example-customer` exists only so `@keboola/validate-ui`'s `visual-brand` axis can assess
 * re-skinning via `?brand=<id>`.
 *
 * - Honouring `?brand=` is deliberate here; most apps treat brand as build config.
 * - Allow-listed: an unknown `?brand=` falls back to `keboola-portal`.
 */
const BRANDS = [keboolaPortal, exampleCustomer];

const resolveBrandId = (): string => {
  const requested = new URLSearchParams(globalThis.location.search).get('brand');
  return BRANDS.find((brand) => brand.id === requested)?.id ?? keboolaPortal.id;
};

/**
 * Controlled scheme, so the choice persists across reloads.
 *
 * - `public/colorScheme.js` reads the same storage key before React mounts.
 */
export function App() {
  const [scheme, setScheme] = useState(readPersistedScheme);

  const changeScheme = (next: ColorScheme) => {
    setScheme(next);
    persistScheme(next);
  };

  return (
    <QueryClientProvider client={queryClient}>
      {/* Without `brands` + `defaultBrandId` no brand tokens are injected and `BrandLogo` is empty. */}
      <ThemeProvider
        brands={BRANDS}
        defaultBrandId={resolveBrandId()}
        scheme={scheme}
        onSchemeChange={changeScheme}
      >
        {/* `ThemeProvider` supplies the `TooltipProvider` that `CopyField.Box` requires. */}
        <RouterProvider router={router} />
      </ThemeProvider>
      {/* eslint-disable-next-line turbo/no-undeclared-env-vars -- Vite built-in, not a real env var */}
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
}
